import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import type { Asset } from '../services/AssetService';

export type ViewerMode = '3D' | '2D' | 'Panorama';
export type ViewerBlockState = {
  mode: ViewerMode;
  message: string;
  progress: number | null;
  startedAt: number;
};

/**
 * An open block with new words and bar — same block, same startedAt, so the hard cap keeps
 * counting. A block of another mode (or none) is returned untouched.
 */
export const withViewerBlockStage = (
  prev: ViewerBlockState | null,
  mode: ViewerMode,
  message: string,
  progress: number | null
): ViewerBlockState | null => {
  if (!prev || prev.mode !== mode) return prev;
  const nextProgress = progress === null ? null : Math.max(0, Math.min(100, progress));
  if (prev.message === message && prev.progress === nextProgress) return prev;
  return { ...prev, message, progress: nextProgress };
};

/**
 * Is there a viewer this mode's loading screen would be waiting for? IFC by COUNT: a site with no
 * IFC files carries `files.ifc = []`, and an empty list is truthy — it armed a 3D block over
 * "No Viewer Files Available" that nothing released until the 5-minute cap (audit 2026-10-05, bug 5).
 */
export const hasViewerForMode = (mode: ViewerMode, s: {
  showAutodeskViewer: boolean;
  show3DViewer: boolean;
  hasIFCFile: boolean;
  showGEOTIFFViewer: boolean;
  hasGeotiff: boolean;
  hasRVTFile: boolean;
  showWorkingE57Viewer: boolean;
  hasE57: boolean;
}): boolean => (
  mode === '3D'
    ? Boolean(s.showAutodeskViewer || s.show3DViewer || s.hasIFCFile)
    : mode === '2D'
      ? Boolean(s.showAutodeskViewer || s.showGEOTIFFViewer || s.hasGeotiff || s.hasRVTFile)
      : Boolean(s.showWorkingE57Viewer || s.hasE57)
);

interface UseViewerBlockerParams {
  activeMenuItem: string;
  /** The site has at least one IFC file (App's hasIFCFile: a COUNT, not the list itself). */
  hasIFCFile: boolean;
  hasRVTFile: boolean;
  selectedAsset: Asset | null;
  show3DViewer: boolean;
  showAutodeskViewer: boolean;
  showGEOTIFFViewer: boolean;
  showWorkingE57Viewer: boolean;
}

/**
 * Viewer blocker state machine + view-mode state, extracted verbatim from App.tsx
 * (structural phase 1, slices A+B). Owns the full-screen "Loading viewer..." block shown
 * while a viewer mode is not yet interactive, the per-mode readiness flags, and the
 * current view mode (state + ref + sync effect). The mode-switch handler
 * (handleViewModeChange) stays in App.tsx — it is entangled with panorama-anchor and
 * overlay flows that this phase must not touch. All state, callbacks, and effects moved
 * unchanged; App.tsx destructures the return under the original names.
 */
export const useViewerBlocker = ({
  activeMenuItem,
  hasIFCFile,
  hasRVTFile,
  selectedAsset,
  show3DViewer,
  showAutodeskViewer,
  showGEOTIFFViewer,
  showWorkingE57Viewer
}: UseViewerBlockerParams) => {
  const { currentLanguage } = useTranslation();

  const [currentViewMode, setCurrentViewMode] = useState<ViewerMode>('3D');
  const currentViewModeRef = useRef<ViewerMode>('3D');
  const [viewerBlockState, setViewerBlockState] = useState<ViewerBlockState | null>(null);
  const [viewerModeReady, setViewerModeReady] = useState<Record<ViewerMode, boolean>>({
    '3D': false,
    '2D': false,
    Panorama: false
  });

  useEffect(() => {
    currentViewModeRef.current = currentViewMode;
  }, [currentViewMode]);

  const getViewerBlockMessage = useCallback((mode: ViewerMode) => {
    if (currentLanguage === 'he') {
      if (mode === 'Panorama') return 'טוען פנורמה...';
      if (mode === '2D') return 'טוען מפה דו-ממדית...';
      return 'טוען מודל תלת-ממדי...';
    }

    if (mode === 'Panorama') return 'Loading panorama viewer...';
    if (mode === '2D') return 'Loading 2D map...';
    return 'Loading 3D viewer...';
  }, [currentLanguage]);

  const beginViewerBlock = useCallback((mode: ViewerMode, message?: string) => {
    setViewerModeReady(prev => prev[mode] === false ? prev : { ...prev, [mode]: false });
    setViewerBlockState({
      mode,
      message: message || getViewerBlockMessage(mode),
      progress: null,
      startedAt: Date.now()
    });
  }, [getViewerBlockMessage]);

  const finishViewerBlock = useCallback((mode?: ViewerMode) => {
    if (mode) {
      setViewerModeReady(prev => prev[mode] === true ? prev : { ...prev, [mode]: true });
    }
    setViewerBlockState(prev => {
      if (!prev) return prev;
      if (mode && prev.mode !== mode) return prev;
      return null;
    });
  }, []);

  const updateViewerBlockProgress = useCallback((mode: ViewerMode, progress: number) => {
    setViewerBlockState(prev => {
      if (!prev || prev.mode !== mode) return prev;
      const normalizedProgress = Math.max(0, Math.min(100, progress));
      return { ...prev, progress: normalizedProgress };
    });
  }, []);

  /** Change what an open block says (and its bar) without restarting it — the hard cap keeps counting. */
  const updateViewerBlockStage = useCallback((mode: ViewerMode, message: string, progress: number | null) => {
    setViewerBlockState(prev => withViewerBlockStage(prev, mode, message, progress));
  }, []);

  useEffect(() => {
    if (activeMenuItem !== 'viewer') {
      finishViewerBlock();
    }
  }, [activeMenuItem, finishViewerBlock]);

  // LAST-RESORT ESCAPE: this full-screen overlay blocks ALL input at z-50000 and previously
  // had no timeout of any kind — any wedge in the readiness signalling chain froze the app
  // forever, hiding even the viewers' own error boxes (stuck-loading investigation
  // 2026-07-13). Every legitimate path releases the block long before this cap; if it ever
  // fires, the block is dropped so whatever is underneath (viewer, error box) becomes
  // visible and interactive, and a warning marks the anomaly for diagnosis.
  useEffect(() => {
    if (!viewerBlockState) return;
    const VIEWER_BLOCK_HARD_CAP_MS = 5 * 60 * 1000;
    const elapsed = Date.now() - viewerBlockState.startedAt;
    const remaining = Math.max(0, VIEWER_BLOCK_HARD_CAP_MS - elapsed);
    const timer = window.setTimeout(() => {
      console.warn(
        `[useViewerBlocker] Hard cap reached (${VIEWER_BLOCK_HARD_CAP_MS / 1000}s) for mode ${viewerBlockState.mode} — releasing the block so the UI underneath is reachable`
      );
      // MUST release WITH the mode: finishViewerBlock() without a mode leaves
      // viewerModeReady[mode]=false and the begin-block effect below immediately re-arms the
      // overlay with a fresh startedAt — an escape that only flickers. Marking the mode ready
      // is what actually stops the re-arm loop.
      finishViewerBlock(viewerBlockState.mode);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [viewerBlockState, finishViewerBlock]);

  useEffect(() => {
    setViewerModeReady({
      '3D': false,
      '2D': false,
      Panorama: false
    });
    setViewerBlockState(null);
  }, [selectedAsset?.id]);

  useEffect(() => {
    if (activeMenuItem !== 'viewer' || viewerModeReady[currentViewMode]) {
      return;
    }

    const hasViewerForCurrentMode = hasViewerForMode(currentViewMode, {
      showAutodeskViewer,
      show3DViewer,
      hasIFCFile,
      showGEOTIFFViewer,
      hasGeotiff: Boolean(selectedAsset?.files?.geotiff),
      hasRVTFile,
      showWorkingE57Viewer,
      hasE57: Boolean(selectedAsset?.files?.e57),
    });

    if (!hasViewerForCurrentMode || viewerBlockState?.mode === currentViewMode) {
      return;
    }

    beginViewerBlock(currentViewMode);
  }, [
    activeMenuItem,
    beginViewerBlock,
    currentViewMode,
    hasIFCFile,
    hasRVTFile,
    selectedAsset?.files?.e57,
    selectedAsset?.files?.geotiff,
    show3DViewer,
    showAutodeskViewer,
    showGEOTIFFViewer,
    showWorkingE57Viewer,
    viewerBlockState?.mode,
    viewerModeReady
  ]);

  return {
    currentViewMode,
    setCurrentViewMode,
    currentViewModeRef,
    viewerBlockState,
    viewerModeReady,
    beginViewerBlock,
    finishViewerBlock,
    updateViewerBlockProgress,
    updateViewerBlockStage
  };
};
